import {
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
  DestroyRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppEvent, EVENT_TYPES } from 'src/app/models/event.model';
import { EventService } from 'src/app/services/event.service';
import { EventRefreshService } from 'src/app/services/event-refresh.service';
import { ConfirmActionModalComponent } from 'src/app/shared/components/confirm-action-modal/confirm-action-modal.component';
import { LogService } from '../../../services/log.service';

@Component({
  selector: 'app-event-list',
  templateUrl: './event-list.component.html',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ConfirmActionModalComponent,
  ],
  styleUrls: ['./event-list.component.css'],
})
export class EventListComponent implements OnInit {
  private log = inject(LogService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  events: AppEvent[] = [];
  filteredEvents: AppEvent[] = [];
  isLoading = true;
  errorMessage = '';

  filterStatus = 'upcoming';
  filterType = '';
  filterPeriod = 'all';
  searchQuery = '';

  eventTypes = EVENT_TYPES;

  // Confirmation modal state
  showConfirm = false;
  selectedEventForDelete: AppEvent | null = null;

  constructor(
    private eventService: EventService,
    private router: Router,
    private refreshService: EventRefreshService,
  ) {}

  ngOnInit(): void {
    this.loadEvents();

    // Mutation réelle → reload HTTP
    this.refreshService.dataChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadEvents());

    // Franchissement d'heure → juste rafraîchir les filtres (resolveDisplayStatus est déjà temps-réel)
    this.refreshService.transitions$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.applyFilters();
        this.refreshService.scheduleTransitions(this.events);
        this.cdr.markForCheck();
      });
  }

  loadEvents(): void {
    this.isLoading = true;
    this.eventService.getEvents({ status: 'all', limit: 200 }).subscribe({
      next: (events) => {
        this.events = events;
        this.applyFilters();
        this.refreshService.scheduleTransitions(events);
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.errorMessage = 'Erreur lors du chargement des événements';
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  applyFilters(): void {
    let result = [...this.events];

    if (this.filterStatus === 'upcoming') {
      result = result.filter((e) => {
        const s = this.resolveDisplayStatus(e);
        return s === 'planned' || s === 'active';
      });
    } else if (this.filterStatus === 'completed') {
      result = result.filter((e) => {
        const s = this.resolveDisplayStatus(e);
        return s === 'completed' || s === 'analyzed';
      });
    }

    if (this.filterType) {
      result = result.filter((e) => e.eventType === this.filterType);
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.trim().toLowerCase();
      result = result.filter((e) => e.name.toLowerCase().includes(q));
    }

    // Filtre par période
    if (this.filterPeriod !== 'all') {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      result = result.filter((e) => {
        const d = new Date(e.eventDate);
        switch (this.filterPeriod) {
          case '7days': {
            const end = new Date(now);
            end.setDate(end.getDate() + 7);
            return d >= now && d <= end;
          }
          case '30days': {
            const end = new Date(now);
            end.setDate(end.getDate() + 30);
            return d >= now && d <= end;
          }
          case '3months': {
            const end = new Date(now);
            end.setMonth(end.getMonth() + 3);
            return d >= now && d <= end;
          }
          case 'past30': {
            const start = new Date(now);
            start.setDate(start.getDate() - 30);
            return d >= start && d < now;
          }
          case 'past3months': {
            const start = new Date(now);
            start.setMonth(start.getMonth() - 3);
            return d >= start && d < now;
          }
          default:
            return true;
        }
      });
    }

    // Tri : à venir d'abord (croissant), passés ensuite (décroissant)
    result.sort((a, b) => {
      const now = new Date();
      const aFuture = new Date(a.eventDate) >= now;
      const bFuture = new Date(b.eventDate) >= now;
      if (aFuture && !bFuture) return -1;
      if (!aFuture && bFuture) return 1;
      const diff =
        new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime();
      return aFuture ? diff : -diff;
    });

    this.filteredEvents = result;
  }

  getEventTypeDetails(type: string) {
    return EVENT_TYPES.find((t) => t.value === type);
  }

  formatDate(date: Date): string {
    return new Intl.DateTimeFormat('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(date));
  }

  getStatusLabel(status: string): string {
    const map: Record<string, string> = {
      planned: 'À venir',
      active: 'En cours',
      completed: 'Terminé',
      analyzed: 'Analysé',
      cancelled: 'Annulé',
    };
    return map[status] || status;
  }

  /** Résout le statut affiché en tenant compte de la date/heure réelle */
  resolveDisplayStatus(event: AppEvent): string {
    if (
      event.status === 'completed' ||
      event.status === 'analyzed' ||
      event.status === 'cancelled'
    ) {
      return event.status;
    }
    const now = new Date();
    const eventDay = new Date(event.eventDate);
    eventDay.setHours(0, 0, 0, 0);
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    if (eventDay < today) return 'completed'; // date passée → terminé
    if (eventDay > today) return 'planned'; // date future → planifié

    // Même jour : vérifier startTime et endTime
    const startTime: string | undefined = (event as any).startTime;
    const endTime: string | undefined = (event as any).endTime;

    const startDt = new Date(event.eventDate);
    if (startTime) {
      const [h, m] = startTime.split(':').map(Number);
      startDt.setHours(h, m, 0, 0);
    } else {
      startDt.setHours(0, 0, 0, 0);
    }

    const endDt = new Date(event.eventDate);
    if (endTime) {
      const [h, m] = endTime.split(':').map(Number);
      endDt.setHours(h, m, 0, 0);
    } else {
      endDt.setHours(23, 59, 0, 0);
    }

    if (endDt <= now) return 'completed'; // terminé
    if (startDt > now) return 'planned'; // pas encore commencé
    return 'active'; // en cours
  }

  getStatusClasses(status: string): string {
    const map: Record<string, string> = {
      planned: 'bg-green-100 text-green-700',
      active: 'bg-purple-100 text-purple-700',
      completed: 'bg-blue-100 text-blue-700',
      analyzed: 'bg-indigo-100 text-indigo-700',
      cancelled: 'bg-gray-100 text-gray-500',
    };
    return map[status] || 'bg-gray-100 text-gray-700';
  }

  viewEvent(id: string): void {
    this.log.debug('navigate to event', id);
    this.router.navigate(['/events', id]);
  }

  isCompleted(event: AppEvent): boolean {
    const s = this.resolveDisplayStatus(event);
    return s === 'completed' || s === 'analyzed';
  }

  viewRapport(id: string): void {
    this.router.navigate(['/events', id, 'rapport']);
  }

  createEvent(): void {
    this.router.navigate(['/events/create']);
  }

  openConfirm(event: AppEvent): void {
    this.selectedEventForDelete = event;
    this.showConfirm = true;
  }

  onConfirmDelete(): void {
    const ev = this.selectedEventForDelete;
    if (!ev) return;
    this.eventService.deleteEvent(ev.id).subscribe({
      next: () => {
        this.events = this.events.filter((e) => e.id !== ev.id);
        this.applyFilters();
        this.showConfirm = false;
        this.selectedEventForDelete = null;
        this.cdr.markForCheck();
      },
      error: () => {
        this.errorMessage = "Impossible de supprimer l'événement.";
        this.showConfirm = false;
        this.selectedEventForDelete = null;
        this.cdr.markForCheck();
      },
    });
  }

  onCancelDelete(): void {
    this.showConfirm = false;
    this.selectedEventForDelete = null;
  }
}
